import type { JSX } from 'react';
import type { RecoveryKit } from '../api/auth';
import { useTokens } from '../state/theme';

interface RecoveryKitDisplayProps {
  kit: RecoveryKit;
  title?: string;
  onDone?: () => void;
  doneLabel?: string;
}

function downloadKit(kit: RecoveryKit): void {
  const body = [
    'Autoscuola AI — kit di recupero',
    `Scadenza: ${kit.expiresAt}`,
    '',
    ...kit.codes,
    '',
    'Ogni codice può essere usato una sola volta.',
  ].join('\n');
  const url = URL.createObjectURL(new Blob([body], { type: 'text/plain;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'kit-recupero-autoscuola-ai.txt';
  link.click();
  URL.revokeObjectURL(url);
}

export function RecoveryKitDisplay({
  kit,
  title,
  onDone,
  doneLabel = 'Chiudi',
}: RecoveryKitDisplayProps): JSX.Element {
  const tokens = useTokens();
  const expiry = new Date(kit.expiresAt);
  const expiryCopy = Number.isNaN(expiry.getTime())
    ? kit.expiresAt
    : expiry.toLocaleDateString('it-IT');

  return (
    <section data-testid="recovery-kit" aria-label="Kit di recupero">
      {title && <h1 style={{ fontSize: 22, margin: '0 0 8px' }}>{title}</h1>}
      <p role="alert" style={{ color: tokens.wrongText, fontSize: 13.5, lineHeight: 1.5, margin: '0 0 12px' }}>
        Questo kit viene mostrato una sola volta. Salvalo ora in un posto sicuro:
        ogni codice funziona una sola volta e sostituisce tutti i kit precedenti.
      </p>
      <ol style={{ margin: '0 0 12px', padding: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
        {kit.codes.map((code) => (
          <li key={code} style={{
            padding: '8px 10px', border: `1px solid ${tokens.line}`,
            borderRadius: tokens.radius.sm, background: tokens.surface,
            color: tokens.text, fontFamily: tokens.fonts.mono, fontSize: 14,
            letterSpacing: 0.4, textAlign: 'center', overflowWrap: 'anywhere',
          }}>
            {code}
          </li>
        ))}
      </ol>
      <p style={{ color: tokens.textMuted, fontSize: 12.5, margin: '0 0 12px' }}>
        Scade il {expiryCopy}.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={() => downloadKit(kit)} style={{
          minHeight: 44, padding: '10px 16px', borderRadius: tokens.radius.pill,
          border: `1px solid ${tokens.lineStrong}`, background: tokens.surface,
          color: tokens.text, fontFamily: tokens.fonts.body, fontWeight: 600,
        }}>
          Scarica il kit
        </button>
        {onDone && (
          <button type="button" onClick={onDone} style={{
            minHeight: 44, padding: '10px 16px', borderRadius: tokens.radius.pill,
            border: 'none', background: tokens.accent, color: '#fff',
            fontFamily: tokens.fonts.body, fontWeight: 700,
          }}>
            {doneLabel}
          </button>
        )}
      </div>
    </section>
  );
}
